/**
 * API utilities for communicating with the Python backend
 */

// Import auth utilities if needed for user information
import { getCurrentUser } from './auth';

// Use relative URL for API requests - this works with the proxy configuration
const API_URL = '/api';

/**
 * Helper function for formatting numbers as currency
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Helper function for formatting dates
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle date strings without timezone conversion issues
  // Parse the YYYY-MM-DD portion from the date string
  const parts = dateString.split('T')[0].split('-');
  if (parts.length === 3) {
    // Create a date using individual components to avoid timezone shifts
    // When creating date this way and setting time to noon, we avoid timezone issues
    const date = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  }
  
  // Fallback to original method if parsing fails
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

/**
 * Add a comment to a scope
 * @param {string} shortName - The short_name of the scope to add the comment to
 * @param {string} content - The content of the comment
 * @param {FileList|File[]|Object[]} [files] - Optional files to attach to the comment
 * @returns {Promise<Object>} Result of the operation
 */
export const addScopeComment = async (shortName, content, files = null) => {
  try {
    // If no files are provided, use simple JSON request
    if (!files || (Array.isArray(files) && files.length === 0)) {
      const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API request failed with status ${response.status}`);
      }
      
      return await response.json();
    }
    
    // Handle file uploads using FormData
    const formData = new FormData();
    formData.append('content', content);
    
    // Handle both FileList and Array of File objects
    if (files) {
      // Case 1: files is a FileList from input[type="file"]
      if (files instanceof FileList) {
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i]);
        }
      }
      // Case 2: files is an array of File objects 
      else if (Array.isArray(files) && files[0] instanceof File) {
        files.forEach(file => formData.append('files[]', file));
      }
      // Case 3: files is already processed file objects with UUID from server
      else if (Array.isArray(files) && typeof files[0] === 'object') {
        formData.append('files', JSON.stringify(files));
      }
    }
    
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/comments`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error adding scope comment:', error);
    throw error;
  }
};

/**
 * Update a comment on a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} commentId - The ID of the comment to update
 * @param {string} content - The new content for the comment
 * @param {FileList|File[]|Object[]} [files] - Optional new files to attach to the comment
 * @param {boolean} [replaceFiles=false] - If true, replace existing files instead of appending
 * @param {Array} [existingFiles=[]] - Array of existing files to keep (used when removing files)
 * @returns {Promise<Object>} Result of the operation
 */
export const updateScopeComment = async (shortName, commentId, content, newFiles = null, replaceFiles = false, existingFiles = []) => {
  try {
    if (!commentId) {
      throw new Error('Comment ID is required');
    }
    
    // No new files being uploaded, just updating content and/or removing files
    if (!newFiles || (Array.isArray(newFiles) && newFiles.length === 0)) {
      const requestData = { 
        content,
        replace_files: replaceFiles
      };
      
      // If replacing files and we have existing files to keep, include them
      if (replaceFiles && existingFiles && existingFiles.length > 0) {
        requestData.existing_files = existingFiles;
      }
      
      const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/comments/${encodeURIComponent(commentId)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        // If response is not JSON, use status text
        errorData = { error: response.statusText };
      }
      
      if (!response.ok) {
        throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
      }
      
      return errorData;
    }
    
    // Handle file uploads using FormData
    const formData = new FormData();
    formData.append('content', content);
    formData.append('replace_files', replaceFiles.toString());
    
    // Include existing files that should be kept
    if (existingFiles && existingFiles.length > 0) {
      formData.append('existing_files', JSON.stringify(existingFiles));
    }
    
    // Handle both FileList and Array of File objects for new files
    if (newFiles) {
      // Case 1: files is a FileList from input[type="file"]
      if (newFiles instanceof FileList) {
        for (let i = 0; i < newFiles.length; i++) {
          formData.append('files[]', newFiles[i]);
        }
      }
      // Case 2: files is an array of File objects 
      else if (Array.isArray(newFiles) && newFiles[0] instanceof File) {
        newFiles.forEach(file => formData.append('files[]', file));
      }
      // Case 3: files is already processed file objects with UUID from server
      else if (Array.isArray(newFiles) && typeof newFiles[0] === 'object') {
        formData.append('files', JSON.stringify(newFiles));
      }
    }
    
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'PUT',
      credentials: 'include',
      body: formData,
    });
    
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      // If response is not JSON, use status text
      errorData = { error: response.statusText };
    }
    
    if (!response.ok) {
      throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
    }
    
    return errorData;
  } catch (error) {
    console.error('Error updating scope comment:', error);
    throw error;
  }
};

/**
 * Delete a comment from a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} commentId - The ID of the comment to delete
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteScopeComment = async (shortName, commentId) => {
  try {
    if (!commentId) {
      throw new Error('Comment ID is required');
    }
    
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      // If response is not JSON, use status text
      errorData = { error: response.statusText };
    }
    
    if (!response.ok) {
      throw new Error(errorData.error || errorData.message || `API request failed with status ${response.status}`);
    }
    
    return errorData;
  } catch (error) {
    console.error('Error deleting scope comment:', error);
    throw error;
  }
};

/**
 * Generic function to update a scope field
 * @param {string} shortName - The short_name of the scope to update
 * @param {string} field - The field to update (e.g., 'subcontractor', 'point_of_contact', 'phone')
 * @param {string} value - The new value for the field
 * @returns {Promise<Object>} Result of the update operation
 */
export const updateScopeField = async (shortName, field, value) => {
  try {
    const response = await fetch(`${API_URL}/update_scope_field`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        short_name: shortName,
        field: field,
        value: value,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating scope ${field}:`, error);
    throw error;
  }
};

/**
 * Fetch construction data from the backend
 * @returns {Promise<Object>} Construction data
 */
export const fetchConstructionData = async () => {
  try {
    const response = await fetch(`${API_URL}/construction_data`, {
      credentials: 'include' // Important for sending cookies with the request
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching construction data:', error);
    throw error;
  }
};

/**
 * Get a specific scope by short_name with detailed information
 * @param {string} shortName - The short_name of the scope to get
 * @returns {Promise<Object|null>} The scope data or null if not found
 */
export const getScopeByShortName = async (shortName) => {
  try {
    // Get detailed scope info from the scope endpoint
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const scope = await response.json();
    
    // Also fetch construction data to get month names
    const data = await fetchConstructionData();
    
    // Get start and end months for display
    const startMonth = scope.timeline_start || 0;
    const duration = scope.timeline_duration || 3;
    
    // Calculate end month index (start + duration - 1 for zero-indexed array)
    // For duration 1, end month is the same as start month
    const endMonthIndex = startMonth + duration - 1;
    
    // Store all months for the month selector
    if (data.month && data.month.length > 0) {
      scope.all_months = data.month;
      
      // Add month names for display
      if (startMonth < data.month.length) {
        scope.start_month_name = data.month[startMonth];
      }
      
      // Ensure end month index is within valid range
      const validEndMonthIndex = Math.min(endMonthIndex, data.month.length - 1);
      if (validEndMonthIndex >= 0 && validEndMonthIndex < data.month.length) {
        scope.end_month_name = data.month[validEndMonthIndex];
      }
    }
    
    return scope;
  } catch (error) {
    console.error('Error getting scope by short_name:', error);
    
    // Fall back to the old method if specific API fails
    try {
      // Fetch all construction data
      const data = await fetchConstructionData();
      
      // Find by short_name
      const scope = data.scope_items.find(scope => scope.short_name === shortName);
      
      if (!scope) return null;
      
      const scopeName = scope.scope;
      
      // Get timeline data if available
      const timeline = data.phase_timings[scopeName];
      if (timeline) {
        scope.timeline_start = timeline.start_month;
        scope.timeline_duration = timeline.duration;
      }
      
      // Get start and end months for display
      const startMonth = scope.timeline_start || 0;
      const duration = scope.timeline_duration || 3;
      
      // Calculate end month index
      const endMonthIndex = startMonth + duration - 1;
      
      // Store all months for the month selector
      if (data.month && data.month.length > 0) {
        scope.all_months = data.month;
        
        // Add month names for display
        if (startMonth < data.month.length) {
          scope.start_month_name = data.month[startMonth];
        }
        
        // Ensure end month index is within valid range
        const validEndMonthIndex = Math.min(endMonthIndex, data.month.length - 1);
        if (validEndMonthIndex >= 0 && validEndMonthIndex < data.month.length) {
          scope.end_month_name = data.month[validEndMonthIndex];
        }
      }
      
      return scope;
    } catch (fallbackError) {
      console.error('Error in fallback method:', fallbackError);
      throw error; // Throw the original error
    }
  }
};

// Alias for backward compatibility
export const getScopeByName = getScopeByShortName;

/**
 * Update a scope's status
 * @param {string} shortName - The short_name of the scope to update
 * @param {string} status - The new status for the scope
 * @returns {Promise<Object>} Result of the update operation
 */
export const updateScopeStatus = async (shortName, status) => {
  try {
    const response = await fetch(`${API_URL}/update_scope_status`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        short_name: shortName,
        status: status,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating scope status:', error);
    throw error;
  }
};

/**
 * Update a scope's completion percentage
 * @param {string} shortName - The short_name of the scope to update
 * @param {number} percentage - The new completion percentage for the scope (0-100)
 * @returns {Promise<Object>} Result of the update operation
 */
export const updateScopeCompletion = async (shortName, percentage) => {
  try {
    // Using the proper write-protected api_update_scope_field endpoint
    const response = await fetch(`${API_URL}/update_scope_field`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        short_name: shortName,
        field: 'percentage_complete',
        value: percentage
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating completion percentage:', error);
    throw error;
  }
};

/**
 * Update the project start date
 * @param {string} project_start_date - The new project start date in YYYY-MM-DD format
 * @returns {Promise<Object>} Result of the update operation
 */
export const updateProjectStartDate = async (project_start_date) => {
  try {
    const response = await fetch(`${API_URL}/update_project_start_date`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        project_start_date
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating project start date:', error);
    throw error;
  }
};

/**
 * Fetch available scenarios
 * @returns {Promise<Array>} List of scenarios
 */
export const fetchScenarios = async () => {
  try {
    const response = await fetch(`${API_URL}/scenarios`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching scenarios:', error);
    throw error;
  }
};

/**
 * Fetch recent activity across all scopes
 * @param {number} limit - Maximum number of activity items to fetch
 * @returns {Promise<Array>} Array of activity items
 */
export const fetchRecentActivity = async (limit = 100) => {
  try {
    const response = await fetch(`${API_URL}/recent_activity?limit=${limit}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    throw error;
  }
};

/**
 * Create a new scope
 * @param {Object} scopeData - The data for the new scope
 * @returns {Promise<Object>} Result of the operation with the new scope's short_name
 */
export const createScope = async (scopeData) => {
  try {
    const response = await fetch(`${API_URL}/create_scope`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(scopeData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating new scope:', error);
    throw error;
  }
};

/**
 * Add a payment to a scope
 * @param {string} shortName - The short_name of the scope
 * @param {Object} paymentData - The payment data (amount, description, date, etc.)
 * @returns {Promise<Object>} Result of the operation
 */
export const addScopePayment = async (shortName, paymentData) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/payments`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error adding scope payment:', error);
    throw error;
  }
};

/**
 * Update a payment on a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} paymentId - The ID of the payment to update
 * @param {Object} paymentData - The updated payment data
 * @returns {Promise<Object>} Result of the operation
 */
export const updateScopePayment = async (shortName, paymentId, paymentData) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/payments/${encodeURIComponent(paymentId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating scope payment:', error);
    throw error;
  }
};

/**
 * Delete a payment from a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} paymentId - The ID of the payment to delete
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteScopePayment = async (shortName, paymentId) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/payments/${encodeURIComponent(paymentId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting scope payment:', error);
    throw error;
  }
};

/**
 * Add a contract to a scope
 * @param {string} shortName - The short_name of the scope
 * @param {Object} contractData - The contract data (amount, description, date, etc.)
 * @returns {Promise<Object>} Result of the operation
 */
export const addScopeContract = async (shortName, contractData) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/contracts`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contractData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error adding scope contract:', error);
    throw error;
  }
};

/**
 * Update a contract on a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} contractId - The ID of the contract to update
 * @param {Object} contractData - The updated contract data
 * @returns {Promise<Object>} Result of the operation
 */
export const updateScopeContract = async (shortName, contractId, contractData) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/contracts/${encodeURIComponent(contractId)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contractData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating scope contract:', error);
    throw error;
  }
};

/**
 * Delete a contract from a scope
 * @param {string} shortName - The short_name of the scope
 * @param {string} contractId - The ID of the contract to delete
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteScopeContract = async (shortName, contractId) => {
  try {
    const response = await fetch(`${API_URL}/scope/${encodeURIComponent(shortName)}/contracts/${encodeURIComponent(contractId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting scope contract:', error);
    throw error;
  }
};

/**
 * Upload a file to the server
 * @param {File} file - The file to upload
 * @returns {Promise<Object>} Result with file info
 */
export const uploadFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}/upload_file`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Get download URL for a file
 * @param {string} fileUuid - The UUID of the file to download
 * @returns {string} URL to download the file
 */
export const getFileUrl = (fileUuid) => {
  return `${API_URL}/files/${encodeURIComponent(fileUuid)}`;
};

/**
 * Delete a file from the server
 * @param {string} fileUuid - The UUID of the file to delete
 * @returns {Promise<Object>} Result of the operation
 */
export const deleteFile = async (fileUuid) => {
  try {
    const response = await fetch(`${API_URL}/files/${encodeURIComponent(fileUuid)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API request failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};